import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { makeStyles, MenuItem, TextField } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { CapaQuotationProductPurpose } from '@generated/graphql';
import useRequest from '@hooks/useRequest';
import { colorSet } from '@components/Provider';
import { projectPurposeOptions } from '@routes/RequestForQuotation/types';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestInputCommonProps } from './types';

const ProjectPurposeSelect: React.FC<RequestInputCommonProps> = ({ error }) => {
  const { requestState } = useRequest();
  const [selectedItem, setSelectedItem] = useState<CapaQuotationProductPurpose>(
    (requestState.projectPurpose as CapaQuotationProductPurpose) ??
      (`` as CapaQuotationProductPurpose)
  );
  const { register, setValue, clearErrors } = useFormContext();
  const { projectPurposeSelect, projectPurposeDetailInput } = useStyles({
    isSelected: Boolean(selectedItem)
  });

  useEffect(() => {
    clearErrors('projectPurpose');
  }, [selectedItem, setValue, clearErrors]);

  return (
    <>
      <input
        name="projectPurpose"
        ref={register({ required: '제품용도를 선택해주세요' })}
        type="hidden"
        value={selectedItem}
      />
      <FormInputLayout label="제품용도" required={true}>
        <TextField
          data-cy="rfq-projectPurpose"
          autoComplete="off"
          className={clsx([projectPurposeSelect, 'purposeInput'])}
          select
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          value={selectedItem}
          SelectProps={{
            displayEmpty: true,
            IconComponent: ExpandMore
          }}
        >
          <MenuItem value={``} disabled className="disabled-item">
            설계하고자 하는 제품의 용도를 선택해주세요.
          </MenuItem>
          {Object.entries(projectPurposeOptions).map(
            (option: [CapaQuotationProductPurpose | string, string], index) => {
              const enumValue = option[0];
              const label = option[1];
              if (['part1', 'part2', 'part3'].includes(enumValue)) {
                return (
                  <MenuItem key={index} disabled>
                    {label}
                  </MenuItem>
                );
              }
              return (
                <MenuItem
                  key={index}
                  value={enumValue}
                  data-cy={`rfq-projectPurpose-item-${enumValue}`}
                  onClick={(e) =>
                    setSelectedItem(enumValue as CapaQuotationProductPurpose)
                  }
                >
                  {label}
                </MenuItem>
              );
            }
          )}
        </TextField>

        {selectedItem && (
          <TextField
            data-cy="rfq-projectPurposeDetail"
            className={projectPurposeDetailInput}
            variant="outlined"
            fullWidth
            placeholder="상세 제품 용도를 적어주세요 (선택)"
            name="projectPurposeDetail"
            inputRef={register}
          />
        )}
      </FormInputLayout>
    </>
  );
};

export default ProjectPurposeSelect;

const useStyles = makeStyles((theme) => ({
  projectPurposeSelect: {
    '& > div > div': {
      padding: '12px 52px 12px 12px',
      color: ({ isSelected }: { isSelected: boolean }) =>
        isSelected ? colorSet.gray900 : colorSet.gray300
    },
    '& input': {
      padding: 12,
      textAlign: 'right'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 8
    },
    '& .disabled-item': {
      color: colorSet.gray500
    }
  },
  projectPurposeDetailInput: {
    paddingTop: 8,
    '& input': {
      padding: 12,
      textAlign: 'left'
    },
    '& .MuiInputBase-root': {
      alignItems: 'center',
      borderRadius: 8
    },
    '& ::placeholder': {
      color: colorSet.gray500
    }
  }
}));
